/* Handles the automatic redirect to the partner site after the `redirectTimeout` period. The navigation will
redirect the user to the `destinationUrl`. This can happen in one of three ways:
1. Using a window.location.href redirect
2. Using the cj.navigate method if the domain is a CJ publisher domain
3. Using a meta tag redirect if the user has Javascript disabled in their browser

If the page should not be automatically redirected, do not include this component in the page. Instead,
the practice has been to include the `destinationUrl` in the DisplayComponent as a link or button for a 
manual user redirect.
*/

'use client';

import Script from 'next/script';
import React from 'react';
import { Logger } from '../../../logging';
import { useIsClient } from '../isClientContext';
import { logWarning, logInfo } from '../logging';
import FailedToNavigateViaCJScriptError from './FailedToNavigateViaCJScript';
import { RedirectContext } from './redirectContext';
import { convertObjToSnakeCase } from '../../lib/utils';

const LOGGER_NAME = 'RedirectComponent';
const REDIRECT_TIME_EXCEEDED_WARNING_TIMEOUT = 3000;

interface Props {
  redirectContext: RedirectContext;
  // Fires the moment we commit to navigating (right before assigning
  // window.parent.location.href / calling window.cj.navigate). Used by the
  // non-Turnstile BounceTracker (UngatedRedirectBeacons) to suppress the
  // `bounced` beacon — a pagehide that fires after this callback is a
  // natural unload from a successful navigation, not a bounce. Skipped in
  // the gated flow where navigation is driven by TurnstileComponent
  // instead.
  onRedirectInitiated?: () => void;
}

const logger = Logger.logger();

function logRedirectStartAndTimeoutMessage(
  logger: Logger,
  loggerName: string,
  context: object,
  redirectTimeExceededWarningTimeout: number
) {
  // This should log once per automatic redirect initiated
  const redirectStartedMessage = 'Starting redirect to partner site';
  logInfo(
    logger,
    loggerName,
    'RedirectStarted',
    redirectStartedMessage,
    context
  );

  // If still on the page after the REDIRECT_TIME_EXCEEDED_WARNING_TIMEOUT, log a warning to
  // Datadog logs. This is to capture redirects that hang on the interstitial page. We keep
  // an eye on the rate of how many redirects started above to how many exceed the
  // REDIRECT_TIME_EXCEEDED_WARNING_TIMEOUT time in light of prod impacts we've encountered:
  // - https://nerdwallet.atlassian.net/browse/PROD-1059
  // - https://nerdwallet.atlassian.net/browse/PROD-1030
  setTimeout(() => {
    const exceededTimeoutMessage = 'Redirect to partner site exceeded timeout';
    logWarning(logger, loggerName, new Error(exceededTimeoutMessage), context);
  }, redirectTimeExceededWarningTimeout);
}

const RedirectComponent: React.FC<Props> = ({
  redirectContext,
  onRedirectInitiated,
}) => {
  const isClient = useIsClient();
  const hasRedirectBeenInitiated = React.useRef(false);

  // Performs a normal redirect if the domain is not a CJ publisher domain
  // Use useEffect to ensure RUM is initialized before logging
  React.useEffect(() => {
    // When skipAutomaticRedirect is set, another component (TurnstileComponent)
    // is responsible for initiating navigation after its own verification
    // completes. We still render so downstream effects (noscript meta refresh,
    // CJ <Script> loading) happen.
    if (redirectContext.skipAutomaticRedirect) {
      return;
    }
    if (isClient && !redirectContext.isCjPublisher && !hasRedirectBeenInitiated.current) {
      hasRedirectBeenInitiated.current = true;
      const timer = setTimeout(() => {
        logRedirectStartAndTimeoutMessage(
          logger,
          LOGGER_NAME,
          convertObjToSnakeCase(redirectContext),
          REDIRECT_TIME_EXCEEDED_WARNING_TIMEOUT
        );
        onRedirectInitiated?.();
        window.parent.location.href = redirectContext.destinationUrl;
      }, redirectContext.redirectWaitTime);

      return () => clearTimeout(timer);
    }
  }, [isClient, redirectContext]);

  /* If the domain is a CJ publisher domain, we use the the CJ script to redirect the user.
    We include a meta tag redirect for users with Javascript disabled; this includes both CJ and non-CJ domains.
  */
  return (
    <>
      <header>
        <noscript>
          <meta
            httpEquiv="refresh"
            content={`1; url=${redirectContext.destinationUrl}`}
          />
        </noscript>
      </header>
      <div>
        {isClient && redirectContext.isCjPublisher && (
          <Script
            src="https://www.p.zjptg.com/tag/2929746/100001"
            strategy="afterInteractive"
            data-testid="cj-script"
            onLoad={() => {
              // In the Turnstile-gated flow we only need the CJ script loaded
              // so window.cj is available when TurnstileComponent performs the
              // post-verification navigation. The navigation itself is driven
              // by TurnstileComponent, not here.
              if (redirectContext.skipAutomaticRedirect) {
                return;
              }
              setTimeout(() => {
                logRedirectStartAndTimeoutMessage(
                  logger,
                  LOGGER_NAME,
                  convertObjToSnakeCase({
                    ...redirectContext,
                    isCjPublisherDomain: redirectContext.isCjPublisher,
                    cjScriptLoaded: true,
                  }),
                  REDIRECT_TIME_EXCEEDED_WARNING_TIMEOUT
                );
                try {
                  if (typeof window.cj?.navigate !== 'function') {
                    throw new Error('window.cj.navigate not available');
                  }
                  onRedirectInitiated?.();
                  window.cj.navigate(redirectContext.destinationUrl);
                } catch {
                  // Fallback to using normal redirects if the CJ script fails to execute
                  // https://nerdwallet.atlassian.net/browse/PP-6968
                  const error = new FailedToNavigateViaCJScriptError(
                    'Failed to execute CJ script. Falling back to normal redirect.'
                  );
                  logWarning(
                    logger,
                    LOGGER_NAME,
                    error,
                    convertObjToSnakeCase(redirectContext)
                  );

                  onRedirectInitiated?.();
                  window.parent.location.href = redirectContext.destinationUrl;
                }
              }, redirectContext.redirectWaitTime);
            }}
            // Fallback to using normal redirects if the CJ script fails to load
            // https://nerdwallet.atlassian.net/browse/PROD-1059
            onError={() => {
              const error = new FailedToNavigateViaCJScriptError(
                'Failed to load CJ script. Falling back to normal redirect.'
              );
              logWarning(
                logger,
                LOGGER_NAME,
                error,
                convertObjToSnakeCase(redirectContext)
              );
              // In the Turnstile-gated flow, TurnstileComponent is responsible
              // for the navigation; it will throw (surfacing a generic error)
              // if it can't resolve window.cj at nav time. We deliberately
              // don't fall back to a direct redirect here — that would bypass
              // Turnstile verification.
              if (redirectContext.skipAutomaticRedirect) {
                return;
              }
              setTimeout(() => {
                logRedirectStartAndTimeoutMessage(
                  logger,
                  LOGGER_NAME,
                  convertObjToSnakeCase({
                    ...redirectContext,
                    isCjPublisherDomain: redirectContext.isCjPublisher,
                    cjScriptLoaded: false,
                  }),
                  REDIRECT_TIME_EXCEEDED_WARNING_TIMEOUT
                );
                onRedirectInitiated?.();
                window.parent.location.href = redirectContext.destinationUrl;
              }, redirectContext.redirectWaitTime);
            }}
          />
        )}
      </div>
    </>
  );
};

export default RedirectComponent;
